import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from '@/shared/ui/skeleton';
import { chartBarCursor, chartLineCursor, chartTooltipProps } from '@/shared/lib/chart-theme';

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--chart-2, var(--accent)))',
  'hsl(var(--muted-foreground))',
];

export interface ChartPoint {
  label: string;
  value: number;
}

interface SeriesChartProps {
  data: ChartPoint[];
  loading?: boolean;
  valueFormatter?: (value: number) => string;
  variant?: 'area' | 'bar';
  height?: number;
}

export function DashboardSeriesChart({
  data,
  loading,
  valueFormatter = (v) => String(v),
  variant = 'area',
  height = 280,
}: SeriesChartProps) {
  if (loading) {
    return <Skeleton className="w-full rounded-xl" style={{ height }} />;
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        Sem dados no período.
      </div>
    );
  }

  const Chart = variant === 'bar' ? BarChart : AreaChart;

  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border/40" vertical={false} />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          className="fill-muted-foreground"
        />
        <YAxis
          tick={{ fontSize: 11 }}
          tickLine={false}
          axisLine={false}
          width={48}
          className="fill-muted-foreground"
        />
        <Tooltip
          formatter={(value: number) => [valueFormatter(value), 'Total']}
          cursor={variant === 'bar' ? chartBarCursor : chartLineCursor}
          {...chartTooltipProps}
        />
        {variant === 'bar' ? (
          <Bar dataKey="value" fill="hsl(var(--primary))" radius={[6, 6, 0, 0]} />
        ) : (
          <Area
            type="monotone"
            dataKey="value"
            stroke="hsl(var(--primary))"
            fill="hsl(var(--primary))"
            fillOpacity={0.15}
            strokeWidth={2}
          />
        )}
      </Chart>
    </ResponsiveContainer>
  );
}

interface GenderSlice {
  name: string;
  value: number;
}

export function DashboardGenderChart({
  data,
  loading,
  height = 260,
}: {
  data: GenderSlice[];
  loading?: boolean;
  height?: number;
}) {
  if (loading) {
    return <Skeleton className="mx-auto max-w-xs rounded-xl" style={{ height }} />;
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
        Sem dados demográficos.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={56} outerRadius={88} paddingAngle={2}>
          {data.map((_, index) => (
            <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number, name: string) => [value, name]} {...chartTooltipProps} />
      </PieChart>
    </ResponsiveContainer>
  );
}
